import { useCallback, useRef, useState, type DragEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiPatch } from '../lib/api';
import { PageHeader, Section, LoadingState, ErrorState, useInsightsQuery } from '../components/ui';

/**
 * Where Ruta's own Terms of Service and Privacy Policy are set — drop a PDF in,
 * or click to pick one, and every surface that shows Ruta's paperwork follows.
 *
 * The only write in admin insights. The app is gated by one shared username and
 * password with no per-person identity, so the record shows when a document was
 * last replaced but never who replaced it — see `admin.md`.
 *
 * This is Ruta's own paperwork. A landscaping company's own Terms & Conditions
 * are a different thing entirely, set in the office app.
 */

type PolicyDocument = 'terms' | 'privacy';

interface Policies {
  /** The document every surface actually opens right now. */
  termsUrl: string;
  privacyUrl: string;
  termsSource: 'configured' | 'built-in';
  privacySource: 'configured' | 'built-in';
  /** What has been uploaded here, or null where nothing has been. */
  configured: { termsUrl: string | null; privacyUrl: string | null };
  /** What the code ships with, used whenever nothing is uploaded. */
  builtIn: { termsUrl: string; privacyUrl: string };
  updatedAt: string | null;
}

interface UploadTarget {
  document: PolicyDocument;
  uploadUrl: string;
  publicUrl: string;
}

const POLICIES_PATH = '/platform-operator/policies';
const POLICIES_KEY = ['platform-operator', 'policies'];

const PDF_TYPE = 'application/pdf';
const MAX_BYTES = 10 * 1024 * 1024;

/** Says whether the document in effect was uploaded here or ships with the code. */
function SourceBadge({ source }: { source: 'configured' | 'built-in' }) {
  const builtIn = source === 'built-in';
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
        builtIn
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-stone-200 bg-stone-100 text-stone-600'
      }`}
    >
      {builtIn ? 'Built-in document' : 'Uploaded here'}
    </span>
  );
}

/**
 * One document: a drop zone, what is live, and where it came from.
 *
 * The file goes straight from the browser to storage using an address the API
 * mints, so a large PDF never travels through the API. Only once it has landed
 * do we tell the API to make it the live document — a failed upload therefore
 * leaves the previous document in place rather than pointing every surface at
 * something that is not there.
 */
function PolicyDocumentCard({
  document,
  label,
  inEffect,
  source,
  onUploaded,
  onCleared,
  busy,
}: {
  document: PolicyDocument;
  label: string;
  inEffect: string;
  source: 'configured' | 'built-in';
  onUploaded: (file: File) => Promise<void>;
  onCleared: () => void;
  busy: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);

  const accept = useCallback(
    (files: FileList | null) => {
      setRejected(null);
      const file = files?.[0];
      if (!file) return;
      if (file.type !== PDF_TYPE) {
        setRejected('Upload a PDF. Other file types are not accepted.');
        return;
      }
      if (file.size > MAX_BYTES) {
        setRejected('That file is larger than 10 MB. Upload a smaller PDF.');
        return;
      }
      void onUploaded(file);
    },
    [onUploaded],
  );

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (busy) return;
    accept(e.dataTransfer.files);
  };

  return (
    <div className="mb-6 last:mb-0">
      <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-stone-400">
        {label}
      </div>

      <div
        role="button"
        tabIndex={0}
        aria-label={`Upload a new ${label}`}
        aria-busy={busy}
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (busy) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
          busy
            ? 'cursor-wait border-stone-200 bg-stone-50'
            : dragOver
              ? 'border-primary bg-primary/5'
              : 'border-stone-300 bg-white hover:border-stone-400 hover:bg-stone-50'
        }`}
      >
        <span className="text-sm font-medium text-stone-700">
          {busy ? 'Uploading…' : `Drop the ${label} here`}
        </span>
        <span className="mt-1 text-xs text-stone-400">
          {busy ? 'This replaces the live document when it finishes.' : 'or click to choose a PDF'}
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => {
          accept(e.target.files);
          e.target.value = '';
        }}
      />

      {rejected && <div className="mt-1.5 text-xs text-red-600">{rejected}</div>}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <SourceBadge source={source} />
        <span className="min-w-0 break-all text-xs text-stone-500">
          Live now:{' '}
          <a
            href={inEffect}
            target="_blank"
            rel="noreferrer"
            className="text-stone-700 underline decoration-stone-300 underline-offset-2 hover:decoration-stone-500"
          >
            open the document
          </a>
        </span>
        {source === 'configured' && (
          <button
            type="button"
            onClick={onCleared}
            disabled={busy}
            className="text-xs text-stone-500 underline decoration-stone-300 underline-offset-2 hover:text-stone-700 disabled:opacity-50"
          >
            Go back to the built-in document
          </button>
        )}
      </div>

      {source === 'built-in' && (
        <p className="mt-1 text-xs text-stone-400">
          Nothing has been uploaded, so the document that ships with the code is being used.
        </p>
      )}
    </div>
  );
}

export function PoliciesPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useInsightsQuery<Policies>(POLICIES_KEY, POLICIES_PATH);

  const [busy, setBusy] = useState<PolicyDocument | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (body: Partial<Record<'termsUrl' | 'privacyUrl', string | null>>) =>
      apiPatch<Policies>(POLICIES_PATH, body),
    onSuccess: (next) => queryClient.setQueryData(POLICIES_KEY, next),
  });

  /**
   * Ask for somewhere to put the file, put it there, then make it live. Each
   * step is allowed to fail without the previous one having changed anything
   * an operator can see.
   */
  const upload = async (document: PolicyDocument, file: File) => {
    setBusy(document);
    setFailure(null);
    setDone(null);
    try {
      const target = await apiPatch<UploadTarget>(POLICIES_PATH, {
        intent: 'upload',
        document,
        contentType: file.type,
        sizeBytes: file.size,
      });

      const put = await fetch(target.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': PDF_TYPE },
        body: file,
      });
      if (!put.ok) throw new Error(`The file could not be stored (${put.status})`);

      await save.mutateAsync(
        document === 'terms' ? { termsUrl: target.publicUrl } : { privacyUrl: target.publicUrl },
      );
      setDone(`${document === 'terms' ? 'Terms of Service' : 'Privacy Policy'} replaced. Every surface uses it now.`);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : 'The upload did not finish. Nothing changed.');
    } finally {
      setBusy(null);
    }
  };

  const clear = (document: PolicyDocument) => {
    setFailure(null);
    setDone(null);
    save.mutate(document === 'terms' ? { termsUrl: null } : { privacyUrl: null }, {
      onSuccess: () => setDone('Back to the document that ships with the code.'),
      onError: (e) => setFailure(e instanceof Error ? e.message : 'That did not save.'),
    });
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;
  if (!data) return <ErrorState error={new Error('No policy documents came back')} />;

  return (
    <div>
      <PageHeader
        title="Policies"
        subtitle="Ruta's own Terms of Service and Privacy Policy"
      />

      <Section
        title="Ruta's terms and privacy documents"
        subtitle="Drop a PDF in and every place Ruta shows its own paperwork follows — the signup agreement, the customer portal, the crew app, and the marketing site's Terms and Privacy pages. This is not a landscaping company's own Terms & Conditions, which are set in the office app."
      >
        <PolicyDocumentCard
          document="terms"
          label="Terms of Service"
          inEffect={data.termsUrl}
          source={data.termsSource}
          onUploaded={(file) => upload('terms', file)}
          onCleared={() => clear('terms')}
          busy={busy === 'terms'}
        />
        <PolicyDocumentCard
          document="privacy"
          label="Privacy Policy"
          inEffect={data.privacyUrl}
          source={data.privacySource}
          onUploaded={(file) => upload('privacy', file)}
          onCleared={() => clear('privacy')}
          busy={busy === 'privacy'}
        />

        {failure && (
          <div className="mb-3">
            <ErrorState error={new Error(failure)} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {done && <span className="text-xs text-stone-500">{done}</span>}
          {data.updatedAt && (
            <span className="text-xs text-stone-400">
              Last replaced {new Date(data.updatedAt).toLocaleString()}
            </span>
          )}
        </div>

        <p className="mt-4 text-xs text-stone-400">
          The marketing site checks for a new document every few minutes. Everywhere else picks it
          up straight away. Admin insights has one shared login, so a change is not recorded against
          a person.
        </p>
      </Section>
    </div>
  );
}
